import React, { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useMemo, useState } from 'react'
import { useFetchLaboratoryTest, useFetchLaboratoryTestProperties } from '~/hooks'
import {
  IFetchLaboratoryTestParamsById,
  IFetchLaboratoryTestPropertiesParams,
  ILaboratoryTest,
  ILaboratoryTestProperty
} from '~/interfaces'

export interface LaboratoryTestContextHelpers {
  isLoading: boolean
  isLoadingProperties: boolean
  errorLaboratoryTest: string
  errorLaboratoryTestProperties: string
  openLaboratoryTestPropertiesComparison: boolean
  setOpenLaboratoryTestPropertiesComparison: Dispatch<SetStateAction<boolean>>
}

export interface LaboratoryTestContextProps extends LaboratoryTestContextHelpers {
  laboratoryTest: ILaboratoryTest
  laboratoryTestProperties: ILaboratoryTestProperty[]
  getLaboratoryTest: (params: IFetchLaboratoryTestParamsById) => void
  getLaboratoryTestProperties: (params: IFetchLaboratoryTestPropertiesParams) => void
  selectedProperties: number[]
  setSelectedProperties: Dispatch<SetStateAction<number[]>>
}

const defaultValue = {
  isLoading: true,
  errorLaboratoryTest: '',
  errorLaboratoryTestProperties: '',
  openLaboratoryTestPropertiesComparison: false,
  laboratoryTest: {} as ILaboratoryTest,
  laboratoryTestProperties: [] as ILaboratoryTestProperty[],
  selectedProperties: [] as number[]
}

const LaboratoryTestContext = createContext<LaboratoryTestContextProps>({
  ...defaultValue
} as LaboratoryTestContextProps)

interface LaboratoryTestProviderProps {
  children: ReactNode
}

const LaboratoryTestProvider: React.FC<LaboratoryTestProviderProps> = ({ children }: LaboratoryTestProviderProps) => {
  const [selectedProperties, setSelectedProperties] = useState<number[]>(defaultValue.selectedProperties)
  const [openLaboratoryTestPropertiesComparison, setOpenLaboratoryTestPropertiesComparison] = useState<boolean>(
    defaultValue.openLaboratoryTestPropertiesComparison
  )

  const [laboratoryFetchParams, setLaboratoryFetchParams] = useState<IFetchLaboratoryTestParamsById | null>(null)
  const { data, isLoading, error: errorLaboratoryTest } = useFetchLaboratoryTest(laboratoryFetchParams)
  const laboratoryTest = useMemo(() => data ?? defaultValue.laboratoryTest, [data])

  const getLaboratoryTest = useCallback((params: IFetchLaboratoryTestParamsById) => {
    setLaboratoryFetchParams(params)
  }, [])

  const [laboratoryPropertiesFetchParams, setLaboratoryPropertiesFetchParams] =
    useState<IFetchLaboratoryTestPropertiesParams | null>(null)
  const {
    data: laboratoryTestProperties,
    isLoading: isLoadingProperties,
    error: errorLaboratoryTestProperties
  } = useFetchLaboratoryTestProperties(laboratoryPropertiesFetchParams)

  const getLaboratoryTestProperties = useCallback((params: IFetchLaboratoryTestPropertiesParams) => {
    setLaboratoryPropertiesFetchParams(params)
  }, [])

  return (
    <LaboratoryTestContext.Provider
      value={{
        isLoading,
        isLoadingProperties,
        laboratoryTest,
        laboratoryTestProperties: laboratoryTestProperties ?? defaultValue.laboratoryTestProperties,
        getLaboratoryTest,
        getLaboratoryTestProperties,
        errorLaboratoryTest: errorLaboratoryTest?.message ?? defaultValue.errorLaboratoryTest,
        errorLaboratoryTestProperties:
          errorLaboratoryTestProperties?.message ?? defaultValue.errorLaboratoryTestProperties,
        selectedProperties,
        setSelectedProperties,
        openLaboratoryTestPropertiesComparison,
        setOpenLaboratoryTestPropertiesComparison
      }}
    >
      {children}
    </LaboratoryTestContext.Provider>
  )
}

const useLaboratoryTest = (): LaboratoryTestContextProps => {
  return React.useContext(LaboratoryTestContext)
}

export { LaboratoryTestContext, LaboratoryTestProvider, useLaboratoryTest }
